var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ensureIsError, rejectWithResErr } from "@iscr/xAppLib/helpers/rejectWithResErr";
import { ErrorMessage } from "@iscr/xAppLib/UIelems/ErrorMessage";
import { Button, Dimmer, Loader, Message, Modal, Table } from "semantic-ui-react";
import Alert from "@iscr/views/NUI/Alert";
import admin_users_model from "@iscr/models/admin_users_model";
import { DataShow, WrappedListPagination } from "@iscr/xAppLib/DataTable";
import { createPortal } from "react-dom";
import { AsyncButton } from "@iscr/views/UIelems/AsyncButton";
import { useDisclosure } from "@iscr/xAppLib/Hooks/useDisclosure";
import hi_model from "@iscr/models/hi/hi_model";
import { IhiNumber } from "@iscr/xAppLib/UIelems/IHI";
const SUSPICIOUS_DUPE_THRESHOLD = 5;
function ResolveDuplicateIHIAlerts({ ihinumber, actions, onCancel, onUpdated }) {
    var _a;
    const [page, setPage] = useState(1);
    const [limit, _setLimit] = useState(5);
    const setLimit = (limit) => {
        setPage(1);
        _setLimit(limit);
    };
    const users = useQuery({
        queryKey: ["admin", "audit", "resolve-duplicate-users", { ihinumber, limit, page }],
        queryFn: () => __awaiter(this, void 0, void 0, function* () {
            return admin_users_model
                .load_users_list({
                filters: {
                    srch_str: ihinumber,
                    extended_ihi: true,
                },
                limit,
                load_data: "db",
                offset: (page - 1) * limit,
                type: "structured",
                get_stats: true,
            })
                .then(rejectWithResErr, ensureIsError)
                .then(res => res.data);
        }),
        placeholderData: data => data,
    });
    const setPrimary = useMutation({
        mutationFn: (_a) => __awaiter(this, [_a], void 0, function* ({ uid }) {
            const confirmed = yield app.confirm("Confirm set primary user", _jsx(ConfirmSetPrimary, { ihinumber: ihinumber }), {
                destructive: true,
                yes: "Continue",
                no: "Cancel",
            });
            if (confirmed !== "yes") {
                return;
            }
            yield hi_model.set_primary_ihi_user({ uid, ihinumber }).then(rejectWithResErr, ensureIsError);
            return yield Promise.all([users.refetch(), onUpdated(uid)]);
        }),
    });
    const setDuplicate = useMutation({
        mutationFn: (_a) => __awaiter(this, [_a], void 0, function* ({ uid }) {
            const confirmed = yield app.confirm("Confirm set user as duplicate", _jsx(ConfirmSetDuplicate, {}), {
                destructive: true,
                yes: "Continue",
                no: "Cancel",
            });
            if (confirmed !== "yes") {
                return;
            }
            yield hi_model.set_duplicate_ihi_user({ uid, ihinumber }).then(rejectWithResErr, ensureIsError);
            return yield Promise.all([users.refetch(), onUpdated(uid)]);
        }),
    });
    const data = Array.isArray(users.data) ? users.data : [];
    const total = Number((_a = data[0]) === null || _a === void 0 ? void 0 : _a.total_count) || 0;
    const pages = Math.ceil(total / limit);
    if (users.isPending) {
        return _jsx(_Fragment, { children: "Loading..." });
    }
    const fields = admin_users_model.LIST_DET_FIELDS_RESOLVE_DUPLICATES;
    return (_jsxs(_Fragment, { children: [_jsx(ErrorMessage, { header: "Loading users", error: users.error, onRetry: users.refetch }), _jsx(ErrorMessage, { header: "Setting primary user", error: setPrimary.error }), _jsx(ErrorMessage, { header: "Setting duplicate user", error: setDuplicate.error }), data.length === 0 && !users.error && _jsx(Message, { info: true, header: "No users" }), data.length > 0 && (_jsx(WrappedListPagination, { compact: true, top: false, bottom: pages > 1, page: page, pages: pages, total: total, pageSize: limit, data: !!users.data, onPageChange: setPage, onPageSizeChange: setLimit, showPageSizeSelector: true, children: _jsxs("div", { className: "relative", children: [_jsx(Dimmer, { active: users.isFetching, inverted: true, children: _jsx(Loader, {}) }), _jsxs(Table, { striped: true, className: "loading", children: [_jsx(Table.Header, { children: _jsxs(Table.Row, { children: [_jsx(Table.HeaderCell, { children: "Resolve as:" }), fields.map(field => (_jsx(Table.HeaderCell, { children: field.name }, field.name)))] }) }), _jsx(Table.Body, { children: data.map(user => (_jsxs(Table.Row, { children: [_jsx(Table.Cell, { collapsing: true, children: _jsxs("div", { className: "flex flex-col gap-2", children: [_jsx(AsyncButton, { type: "button", color: "olive", onClick: () => setPrimary.mutateAsync({ uid: user.uid }), content: "Primary" }), user.ihi_can_set_duplicate && (_jsx(AsyncButton, { type: "button", color: "brown", onClick: () => setDuplicate.mutateAsync({ uid: user.uid }), content: "Duplicate" }))] }) }), fields.map(field => (_jsx(Table.Cell, { children: DataShow.show_data_field(user, field, undefined, undefined, undefined, undefined, {
                                                    refresh: users.refetch,
                                                }) }, field.name)))] }, user.uid))) })] })] }) })), actions &&
                createPortal(_jsxs("div", { className: "flex flex-col", children: [total > SUSPICIOUS_DUPE_THRESHOLD && (_jsxs(Alert, { warning: true, header: "Excessive duplicates", className: "text-left", children: ["There are ", total, " duplicate records for this IHI. This may be an attempt to circumvent various platform restrictions and prescribing guards."] })), _jsx("div", { children: _jsx(Button, { type: "button", onClick: onCancel, children: "Cancel" }) })] }), actions)] }));
}
export function ResolveDuplicateIHIAlertsPopper(_a) {
    var { subtle, refresh } = _a, props = __rest(_a, ["subtle", "refresh"]);
    const [actionsRef, setActionsRef] = useState(null);
    const { onOpen, onClose, isOpen } = useDisclosure();
    if (!props.ihinumber) {
        return null;
    }
    const trigger = _jsx(Button, { basic: subtle, type: "button", color: "orange", content: "Review duplicate IHIs" });
    return (_jsxs(Modal, { trigger: trigger, size: "fullscreen", open: isOpen, onClose: onClose, onOpen: onOpen, closeIcon: true, children: [_jsx(Modal.Header, { closeButton: true, children: "Resolve Duplicate IHIs" }), _jsx(Modal.Content, { scrolling: true, children: _jsx(ResolveDuplicateIHIAlerts, Object.assign({}, props, { actions: actionsRef, onCancel: onClose, onUpdated: () => __awaiter(this, void 0, void 0, function* () {
                        yield (refresh === null || refresh === void 0 ? void 0 : refresh());
                        onClose();
                    }) })) }), _jsx(Modal.Actions, { children: _jsx("span", { ref: setActionsRef }) })] }));
}
function ConfirmSetPrimary({ ihinumber }) {
    return (_jsxs(_Fragment, { children: [_jsxs("p", { children: ["Setting this user as the primary IHI ", _jsx(IhiNumber, { plain: true, value: ihinumber }), " user will prevent IHI duplicate alerts from being raised in future for requests submitted from this account."] }), _jsx("p", { children: "This will not prevent other user accounts with the same IHI from submitting requests in future, however it will cause another alert to be raised if the other accounts submit a request." }), _jsx("p", { children: _jsx("small", { children: "(Ideally, patients should not have multiple accounts, but it is what it is.)" }) }), _jsx("p", { children: "You will need to reprocess any scripts blocked due to this IHI alert." })] }));
}
function ConfirmSetDuplicate() {
    return (_jsx(_Fragment, { children: _jsx("p", { children: "You will need to reprocess any scripts blocked due to this IHI alert." }) }));
}
